import * as Sentry from "@sentry/vue";

type SessionData = Record<string, any> | null;

export function useSessionData() {
  const { uuid } = useHelpers();
  const nuxtApp = useNuxtApp();
  let sessionData: SessionData = null;

  // PCI DSS (Nessus QID 150122/150123): the session id cookie must carry
  // HttpOnly + Secure + SameSite so page scripts can neither read nor forge
  // it.
  // `secure` defaults to on (all deployments are HTTPS only); local HTTP dev
  // gets an insecure cookie so the feature still works there. Can be pinned
  // per environment with NUXT_SESSION_COOKIE_SECURE=true/false.
  // (The override is a private runtime-config key, readable on the server
  // only — the `secure` flag only affects the server-side Set-Cookie anyway.)
  const sessionCookieSecure = (() => {
    if (import.meta.server) {
      const config = useRuntimeConfig();
      if (config.sessionCookieSecure === "true") return true;
      if (config.sessionCookieSecure === "false") return false;
    }
    return process.env.NODE_ENV !== "development";
  })();

  const sessionId = useCookie("NUXT_SESSION_ID", {
    httpOnly: true,
    secure: sessionCookieSecure,
    sameSite: "lax",
  });

  // HttpOnly cookies are invisible to client-side JavaScript, so the id is
  // handed to the client through the SSR payload (serialized into
  // __NUXT_DATA__). The cookie itself is only ever written on the server:
  // the client never assigns to the ref, so Nuxt will not rewrite the
  // cookie without the HttpOnly attribute.
  let currentId: string | undefined;
  if (import.meta.server) {
    if (!sessionId.value) {
      sessionId.value = uuid();
    }
    if (!nuxtApp.payload.sessionId) {
      nuxtApp.payload.sessionId = sessionId.value;
    }
    currentId = sessionId.value;
  } else {
    currentId = nuxtApp.payload.sessionId as string | undefined;
  }

  const redisKey = currentId ? `session-${currentId}` : null;

  const getData = async (): Promise<SessionData> => {
    if (!redisKey) {
      return null;
    }

    try {
      const response = await $fetch("/api/redis", {
        method: "GET",
        params: { key: redisKey },
      });

      if (
        response.success &&
        "value" in response &&
        typeof response.value === "string"
      ) {
        return JSON.parse(response.value);
      } else {
        return null;
      }
    } catch (error) {
      Sentry.captureEvent({
        message: `Failed to fetch session data`,
        level: "warning",
      });

      return null;
    }
  };

  const persistData = async () => {
    if (!redisKey) {
      return false;
    }

    try {
      const response = await $fetch("/api/redis", {
        method: "POST",
        body: {
          key: redisKey,
          value: sessionData === null ? null : JSON.stringify(sessionData),
          ttl: 60 * 60, // 1 hour
        },
      });

      if (!response.success) {
        Sentry.captureEvent({
          message: `Failed to persist session data`,
          level: "error",
        });
      }

      return response.success;
    } catch (error) {
      Sentry.captureEvent({
        message: `Failed to persist session data`,
        level: "error",
      });

      return false;
    }
  };

  const getItem = async (key: string) => {
    if (sessionData === null) {
      sessionData = await getData();
    }

    if (!sessionData) {
      return null;
    }

    if (key in sessionData) {
      return sessionData[key];
    } else {
      return null;
    }
  };

  const setItem = async (key: string, value: any) => {
    if (sessionData === null) {
      sessionData = await getData();
    }

    if (!sessionData) {
      sessionData = {};
    }

    sessionData[key] = value;
    await persistData();
  };

  const setItems = async (newItems: SessionData) => {
    if (sessionData === null) {
      sessionData = await getData();
    }

    if (!sessionData) {
      sessionData = {};
    }

    sessionData = Object.assign(sessionData, newItems);
    await persistData();
  };

  const terminate = async () => {
    sessionData = null;
    await persistData();
  };

  const removeItem = async (key: string) => {
    if (sessionData === null) {
      sessionData = await getData();
    }

    if (!sessionData) {
      sessionData = {};
    }

    delete sessionData[key];

    if (Object.keys(sessionData).length === 0) {
      // Terminate session
      sessionData = null;
    }

    await persistData();
  };

  return {
    getItem,
    setItem,
    setItems,
    removeItem,
    terminate,
  };
}
